import type { CSSObject } from '@emotion/styled';
import { Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { AnimeatableIconRef } from '@parkdna/ui/icons/types';
import { useTheme } from '@parkdna/ui/theme';
import type { LucideIcon } from 'lucide-react';
import type { ForwardRefExoticComponent, RefAttributes } from 'react';
import { useCallback, useRef } from 'react';
import { MenuItem } from 'react-pro-sidebar';
import { NavLink, useMatch } from 'react-router-dom';

import { useSidebarStore } from '@/stores/sidebar';

import MenuIcon, { type MenuIconRef } from './MenuIcon';

type AnimatedIconComponent = ForwardRefExoticComponent<
  { size?: number } & RefAttributes<AnimeatableIconRef>
>;

type IconComponent = AnimatedIconComponent | LucideIcon;

export type SidebarMenuItemProps = {
  title: string;
  to: string;
  iconComponent?: IconComponent;
  titleVariant?: React.ComponentProps<typeof Typography>['variant'];
  rootStyles?: CSSObject;
  dataCy?: string;
  dataTestId?: string;
  /**
   * If true, only match when the location exactly matches the `to` path.
   * Use for child routes that shouldn't match when on sibling routes.
   * Default: false (allows parent to stay highlighted on child routes)
   */
  end?: boolean;
};

export default function SidebarMenuItem({
  title,
  to,
  iconComponent: Icon,
  titleVariant = 'h5',
  rootStyles,
  dataCy,
  dataTestId,
  end = false,
}: SidebarMenuItemProps) {
  const [theme] = useTheme();
  // Use exact matching only when specified (e.g., for child routes with siblings)
  const isActive = !!useMatch({ path: to, end });
  const isSidebarCollapsed = useSidebarStore((state) => state.isCollapsed);
  const isOverlay = useMediaQuery(theme.breakpoints.down('md'));
  const menuIconRef = useRef<MenuIconRef>(null);

  const shouldShowTooltip = isSidebarCollapsed && !isOverlay;

  const handleMouseEnter = useCallback(() => {
    menuIconRef.current?.startAnimation();
  }, []);

  const handleMouseLeave = useCallback(() => {
    menuIconRef.current?.stopAnimation();
  }, []);

  return (
    <MenuItem
      style={{
        color: theme.palette.text.primary,
        display: 'flex',
        alignItems: 'center',
      }}
      component={<NavLink to={to} end={end} />}
      icon={
        Icon ? (
          <MenuIcon
            ref={menuIconRef}
            Icon={Icon}
            title={title}
            shouldShowTooltip={shouldShowTooltip}
            size={18}
          />
        ) : null
      }
      rootStyles={rootStyles}
      data-cy={dataCy}
      data-testid={dataTestId}
      active={isActive}
      title={isSidebarCollapsed ? title : undefined}
      aria-label={isSidebarCollapsed ? title : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Typography
        variant={titleVariant}
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          verticalAlign: 'middle',
          lineHeight: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textTransform: 'none',
        }}
      >
        {title}
      </Typography>
    </MenuItem>
  );
}
